<template>
    <TVJoyDialog pattern-confirm size="large">
        <template #dialog-header>{{ $t('projectclient.quote.view.quote-summary.dialog.billing-information.title') }}</template>
        <template #dialog-subheader>{{ $t('projectclient.quote.view.quote-summary.dialog.billing-information.subtitle') }}</template>
        <template #dialog-body>
            <InjectMicroFrontendApp
                app="compliance-mf-billing-information"
                :props="{
                    page: 'quote_acceptance_modal',
                    companyId,
                    showLegalInformationStatus: true,
                    showSaveButton: false,
                    layoutVariant: 'form-only',
                    invalidateOnVatMismatch: true,
                }"
                @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
                @billing-information-update-failed="onBillingInformationUpdateFailed"
                @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
            >
                <template #loading>
                    <BillingInformationSkeleton />
                </template>
            </InjectMicroFrontendApp>
        </template>

        <template #dialog-cancel>
            <VJoyButton variant="ghost" data-testid="billing-information-modal-cancel" @click="closeDialog">{{ $t('general.cancel') }}</VJoyButton>
        </template>
        <template #dialog-confirm>
            <VJoyButton
                variant="main"
                data-testid="billing-information-modal-confirm"
                :disabled="!billingFormValid || mfSavePending || loadingConfirm"
                :loading="mfSavePending || loadingConfirm"
                @click="handleQuoteApproval"
            >
                {{ $t('general.confirm') }}
            </VJoyButton>
        </template>
    </TVJoyDialog>
</template>

<script setup lang="ts">
    import {onMounted, ref} from 'vue';
    import {InjectMicroFrontendApp} from '#components';
    import {useAmplitudeSessionReplay, useDataTracking, useMFEvent, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyButton} from '@maltjoy/core-vue';
    import {useDialogRoot} from '~/common/composables';
    import {BillingInformationSkeleton} from '~/common/ui';

    const {TVJoyDialog, confirmDialog, closeDialog, loadingConfirm} = useDialogRoot();

    useAmplitudeSessionReplay();

    const props = defineProps<{
        companyId: string;
        quoteId: string;
    }>();

    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {dispatchMFEvent} = useMFEvent('compliance-mf-billing-information');

    onMounted(() => {
        track('client_billing_information_modal_viewed', {quote_id: props.quoteId});
    });

    type BillingInformationFailureReason = 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR';

    const PREFIX = 'projectclient.quote.view.quote-summary.dialog.billing-information';

    const billingFormValid = ref(false);
    const mfSavePending = ref(false);
    const awaitingSaveResult = ref(false);

    function onBillingInformationFilledStateChanged(state: boolean) {
        billingFormValid.value = state;
    }

    function onBillingInformationUpdatedSuccessfully() {
        mfSavePending.value = false;
        if (!awaitingSaveResult.value) {
            return;
        }
        awaitingSaveResult.value = false;
        confirmDialog();
    }

    function showBillingUpdateErrorSnackbar(failureReason: BillingInformationFailureReason) {
        let snackbarProps: {header?: string; message: string};

        switch (failureReason) {
            case 'INVALID_BILLING_INFORMATION':
                snackbarProps = {
                    header: t(`${PREFIX}.error.INVALID_BILLING_INFORMATION.header`),
                    message: t(`${PREFIX}.error.INVALID_BILLING_INFORMATION.message`, [props.companyId]),
                };
                break;
            case 'INSUFFICIENT_PERMISSIONS':
                snackbarProps = {
                    message: t(`${PREFIX}.error.INSUFFICIENT_PERMISSIONS.message`),
                };
                break;
            default:
                snackbarProps = {
                    message: t(`${PREFIX}.error.SERVER_ERROR.message`),
                };
        }

        pushVJoySnackbar({
            props: {
                ...snackbarProps,
                level: 'error',
            },
        });
    }

    function onBillingInformationUpdateFailed(failureReason: BillingInformationFailureReason) {
        mfSavePending.value = false;
        awaitingSaveResult.value = false;
        showBillingUpdateErrorSnackbar(failureReason);
    }

    function handleQuoteApproval() {
        mfSavePending.value = true;
        awaitingSaveResult.value = true;
        dispatchMFEvent('trigger-save');
    }
</script>

<style lang="scss" scoped>
    .modal {
        &__description {
            margin-bottom: var(--joy-core-spacing-8);
        }
    }
</style>
